import { useCallback, useRef } from 'react';
import { toNumberOrNull } from '@/lib/number';
import type { PondOverview } from '@/types/api';
import type { StatusColor } from './pondStatus';

// Water blue gradients — all ponds look like water, status shown via border + dot
const WATER_GRADIENTS: Record<StatusColor, [string, string, string]> = {
  green: ['#4BA3C7', '#2E7D9E', '#1B5A75'],
  amber: ['#5B94A8', '#3D7A8E', '#2A5E6E'],
  red:   ['#6B8A9A', '#4A6E7E', '#2D4F5E'],
  gray:  ['#7A8B92', '#5A6E76', '#3E525A'],
};

const BORDER_COLORS: Record<StatusColor, string> = {
  green: '#22c55e',
  amber: '#f59e0b',
  red:   '#ef4444',
  gray:  '#6b7280',
};

const STATUS_DOT_COLORS: Record<StatusColor, string> = {
  green: '#4ade80',
  amber: '#fbbf24',
  red:   '#f87171',
  gray:  '#9ca3af',
};

const GLOW_COLORS: Record<StatusColor, string> = {
  green: 'rgba(74, 222, 128, 0.5)',
  amber: 'rgba(251, 191, 36, 0.5)',
  red:   'rgba(248, 113, 113, 0.5)',
  gray:  'rgba(156, 163, 175, 0.3)',
};

interface PondShapeProps {
  pond: PondOverview;
  x: number;
  y: number;
  w: number;
  h: number;
  status: StatusColor;
  isEditMode: boolean;
  isHovered: boolean;
  onHoverStart: (pond: PondOverview, svgX: number, svgY: number) => void;
  onHoverEnd: () => void;
  onClick: (pond: PondOverview) => void;
  onDragStart: (pondId: string, e: React.PointerEvent) => void;
  onResizeStart: (pondId: string, e: React.PointerEvent) => void;
}

export function PondShape({
  pond,
  x,
  y,
  w,
  h,
  status,
  isEditMode,
  isHovered,
  onHoverStart,
  onHoverEnd,
  onClick,
  onDragStart,
  onResizeStart,
}: PondShapeProps) {
  const [c1, c2, c3] = WATER_GRADIENTS[status];
  const gradId = `pg-${pond.id}`;
  const glowId = `glow-${pond.id}`;
  const rippleId = `rip-${pond.id}`;

  const cycle = pond.active_cycle;
  const doc = cycle?.doc;
  const feeding = cycle?.last_feeding;
  const feedAmt = toNumberOrNull(feeding?.feed_amount_kg ?? null);
  const groupRef = useRef<SVGGElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (isEditMode) onDragStart(pond.id, e);
    },
    [isEditMode, onDragStart, pond.id],
  );

  const handleClick = useCallback(() => {
    if (!isEditMode) onClick(pond);
  }, [isEditMode, onClick, pond]);

  const handleMouseEnter = useCallback(() => {
    onHoverStart(pond, x + w / 2, y);
  }, [onHoverStart, pond, x, y, w]);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      onResizeStart(pond.id, e);
    },
    [onResizeStart, pond.id],
  );

  // Resize handle size
  const HS = 10;
  const nameFS = w < 100 ? 10 : 12;

  return (
    <g
      ref={groupRef}
      data-pond={pond.id}
      transform={`translate(${x}, ${y})`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHoverEnd}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      style={{ cursor: isEditMode ? 'grab' : 'pointer' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%" stopColor={c1} />
          <stop offset="50%" stopColor={c2} />
          <stop offset="100%" stopColor={c3} />
        </linearGradient>

        <filter id={glowId} x="-25%" y="-25%" width="150%" height="150%">
          <feDropShadow dx="0" dy="0" stdDeviation="8" floodColor={GLOW_COLORS[status]} />
        </filter>

        <pattern id={rippleId} x="0" y="0" width={w} height="20" patternUnits="userSpaceOnUse">
          <path
            d={`M0,10 Q${w * 0.12},6 ${w * 0.25},10 T${w * 0.5},10 T${w * 0.75},10 T${w},10`}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="0.8"
          />
        </pattern>
      </defs>

      {/* Earth embankment shadow */}
      <rect
        x={-3} y={-3}
        width={w + 6} height={h + 6}
        rx="5" ry="5"
        fill="#8B7355"
        opacity="0.4"
      />

      {/* Main water body */}
      <rect
        width={w} height={h}
        rx="4" ry="4"
        fill={`url(#${gradId})`}
        stroke={isHovered ? GLOW_COLORS[status] : BORDER_COLORS[status]}
        strokeWidth={isHovered ? 3 : 2}
        filter={isHovered ? `url(#${glowId})` : undefined}
      />

      {/* Ripple overlay */}
      <rect
        width={w} height={h}
        rx="4" ry="4"
        fill={`url(#${rippleId})`}
      />

      {/* Light reflection */}
      <ellipse
        cx={w * 0.25} cy={h * 0.25}
        rx={w * 0.18} ry={h * 0.12}
        fill="rgba(255,255,255,0.06)"
      />

      {/* Pond name — centered */}
      <text
        x={w / 2}
        y={h / 2 - (doc != null ? 8 : 0) - (feeding ? 6 : 0)}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#ffffff"
        fontSize={nameFS}
        fontWeight="600"
        style={{ pointerEvents: 'none', userSelect: 'none', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
      >
        {pond.name}
      </text>

      {/* DOC label */}
      {doc != null && (
        <text
          x={w / 2}
          y={h / 2 + (feeding ? -2 : 6)}
          textAnchor="middle"
          dominantBaseline="central"
          fill="rgba(255,255,255,0.7)"
          fontSize="10"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          DOC {doc}
        </text>
      )}

      {/* Feeding info */}
      {feeding && h > 55 && (
        <text
          x={w / 2}
          y={h / 2 + (doc != null ? 12 : 6)}
          textAnchor="middle"
          dominantBaseline="central"
          fill="rgba(255,255,255,0.6)"
          fontSize="9"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          Feed: {feedAmt != null ? `${feedAmt.toFixed(1)} kg` : '—'} · {feeding.feeding_slot}
        </text>
      )}

      {/* Idle state */}
      {pond.status !== 'active' && (
        <text
          x={w / 2}
          y={h / 2 + 8}
          textAnchor="middle"
          dominantBaseline="central"
          fill="rgba(255,255,255,0.4)"
          fontSize="9"
          fontStyle="italic"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {pond.status === 'preparing' ? 'Preparing' : 'No active cycle'}
        </text>
      )}

      {/* Status dot */}
      <circle
        cx={w - 12} cy={12}
        r={5}
        fill={STATUS_DOT_COLORS[status]}
        stroke="rgba(0,0,0,0.3)"
        strokeWidth="1"
      />

      {/* Edit mode: drag handle (top-left) + resize handle (bottom-right) */}
      {isEditMode && (
        <>
          {/* Drag handle dots */}
          <g opacity="0.6">
            <circle cx={10} cy={10} r={2} fill="#fff" />
            <circle cx={16} cy={10} r={2} fill="#fff" />
            <circle cx={10} cy={16} r={2} fill="#fff" />
            <circle cx={16} cy={16} r={2} fill="#fff" />
          </g>

          {/* Resize handle (bottom-right corner) */}
          <g
            onPointerDown={handleResizePointerDown}
            style={{ cursor: 'nwse-resize' }}
          >
            <rect
              x={w - HS} y={h - HS}
              width={HS} height={HS}
              rx="2" ry="2"
              fill="rgba(255,255,255,0.8)"
              stroke="rgba(0,0,0,0.3)"
              strokeWidth="1"
            />
            {/* Diagonal lines inside handle */}
            <line x1={w - 3} y1={h - 7} x2={w - 7} y2={h - 3} stroke="rgba(0,0,0,0.4)" strokeWidth="1" />
            <line x1={w - 3} y1={h - 4} x2={w - 4} y2={h - 3} stroke="rgba(0,0,0,0.4)" strokeWidth="1" />
          </g>
        </>
      )}
    </g>
  );
}
